// The reader's watches as page state — list, selection, the selected watch's
// detail + readout document, polling while Merlin is at work, and the
// lifecycle actions. Shared by the Watches page (admin oversight) and the
// Briefing page, where the watches — the daily briefing first — sit as tabs
// beside the chat (kanban 14927fad, 001b6e2b).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { anyOpenRun, sortWatches } from '../../pages/merlinWatchesUtils';
import {
  merlinWatchesApi,
  type Watch,
  type WatchCadence,
  type WatchDetail,
  type WatchKind,
} from '../../services/merlinWatches';
import { signalsReportsApi } from '../../services/signalsReports';

export const NEW_WATCH = '__new__';
const POLL_MS = 20_000;

export interface UseWatchesOptions {
  /** 'newest': land on the first watch in rail order (the daily briefing
   * leads it), or the new-watch form when there is none. 'none': the daily
   * briefing when there is one, else nothing until the reader picks (the
   * Briefing page then offers to start the briefing). */
  autoSelect: 'newest' | 'none';
  /** A selection to start from (e.g. `?watch=<id>` on the Briefing page). */
  initialSelected?: string | null;
}

export interface WatchesState {
  items: Watch[];
  sorted: Watch[];
  /** A watch id, NEW_WATCH, or null (nothing selected). */
  selected: string | null;
  select: (id: string | null) => void;
  detail: WatchDetail | null;
  html: string | null;
  loading: boolean;
  error: string | null;
  note: string | null;
  setNote: (note: string | null) => void;
  busy: boolean;
  polling: boolean;
  refreshAll: () => Promise<void>;
  createWatch: (
    subject: string,
    cadence: WatchCadence,
    kind?: WatchKind,
  ) => Promise<WatchDetail | null>;
  refine: (text: string) => Promise<void>;
  runNow: () => Promise<void>;
  togglePause: () => Promise<void>;
  setCadence: (cadence: WatchCadence) => Promise<void>;
  remove: () => Promise<void>;
}

export function useWatches(
  scope: 'mine' | 'all',
  { autoSelect, initialSelected = null }: UseWatchesOptions,
): WatchesState {
  const [items, setItems] = useState<Watch[]>([]);
  const [selected, setSelected] = useState<string | null>(initialSelected);
  const [detail, setDetail] = useState<WatchDetail | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const sorted = useMemo(() => sortWatches(items), [items]);

  const loadList = useCallback(async () => {
    setError(null);
    try {
      const list = await merlinWatchesApi.list(scope);
      setItems(list);
      return list;
    } catch {
      setError('Could not load your watches.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [scope]);

  const loadDetail = useCallback(async (id: string) => {
    try {
      setDetail(await merlinWatchesApi.get(id));
    } catch {
      setDetail(null);
      setNote('Could not load that watch.');
    }
  }, []);

  // First load (and on scope change): settle the selection against the list.
  useEffect(() => {
    void (async () => {
      const list = await loadList();
      if (!list) return;
      setSelected((cur) => {
        if (cur === NEW_WATCH) return cur;
        if (cur && list.some((w) => w.id === cur)) return cur;
        if (list.length > 0 && (autoSelect === 'newest' || list.some((w) => w.kind === 'briefing'))) {
          return sortWatches(list)[0].id;
        }
        return autoSelect === 'newest' ? NEW_WATCH : null;
      });
    })();
  }, [loadList, autoSelect]);

  useEffect(() => {
    if (!selected || selected === NEW_WATCH) {
      setDetail(null);
      return;
    }
    void loadDetail(selected);
  }, [selected, loadDetail]);

  // While Merlin is at work on any watch, poll so the readout lands by itself.
  const polling = anyOpenRun(items);
  useEffect(() => {
    if (!polling) return undefined;
    const t = window.setInterval(() => {
      void loadList();
      if (selected && selected !== NEW_WATCH) void loadDetail(selected);
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [polling, loadList, loadDetail, selected]);

  // The readout document whenever the selected watch's latest readout changes.
  const latestId = detail?.watch.latest?.id ?? null;
  useEffect(() => {
    if (!latestId) {
      setHtml(null);
      return undefined;
    }
    let active = true;
    signalsReportsApi
      .fetchHtml(latestId)
      .then((doc) => {
        if (active) setHtml(doc);
      })
      .catch(() => {
        if (active) setHtml(null);
      });
    return () => {
      active = false;
    };
  }, [latestId]);

  const refreshAll = useCallback(async () => {
    await loadList();
    if (selected && selected !== NEW_WATCH) await loadDetail(selected);
  }, [loadList, loadDetail, selected]);

  const createWatch = useCallback(
    async (subject: string, cadence: WatchCadence, kind: WatchKind = 'watch') => {
      const cleaned = subject.trim();
      if (cleaned.length < 3) return null;
      setBusy(true);
      setNote(null);
      try {
        const d = await merlinWatchesApi.create(cleaned, cadence, kind);
        setDetail(d);
        setSelected(d.watch.id);
        await loadList();
        setNote(
          kind === 'briefing'
            ? 'Daily briefing started. Merlin is writing the first one — it takes a few minutes.'
            : 'Watch set. Merlin is writing the first readout — it takes a few minutes.',
        );
        return d;
      } catch {
        setNote(kind === 'briefing' ? 'Could not start your daily briefing.' : 'Could not set that watch up.');
        return null;
      } finally {
        setBusy(false);
      }
    },
    [loadList],
  );

  const refine = useCallback(
    async (text: string) => {
      if (!detail) return;
      const cleaned = text.trim();
      if (cleaned.length < 2) return;
      setBusy(true);
      setNote(null);
      try {
        const d = await merlinWatchesApi.refine(detail.watch.id, cleaned);
        setDetail(d);
        await loadList();
        setNote(
          d.watch.open_run && d.watch.open_run.trigger === 'refinement'
            ? 'Noted. Merlin is rewriting the readout with that applied.'
            : 'Noted. Merlin is mid-check; the next readout will carry it.',
        );
      } catch {
        setNote('Could not record that.');
      } finally {
        setBusy(false);
      }
    },
    [detail, loadList],
  );

  const runNow = useCallback(async () => {
    if (!detail) return;
    setBusy(true);
    setNote(null);
    try {
      const res = await merlinWatchesApi.runNow(detail.watch.id);
      setNote(res.detail);
      await refreshAll();
    } catch {
      setNote('Could not queue a check — is Merlin already on it?');
    } finally {
      setBusy(false);
    }
  }, [detail, refreshAll]);

  const togglePause = useCallback(async () => {
    if (!detail) return;
    setBusy(true);
    setNote(null);
    try {
      if (detail.watch.status === 'paused') await merlinWatchesApi.resume(detail.watch.id);
      else await merlinWatchesApi.pause(detail.watch.id);
      await refreshAll();
    } catch {
      setNote('Could not change that watch.');
    } finally {
      setBusy(false);
    }
  }, [detail, refreshAll]);

  const setCadence = useCallback(
    async (cadence: WatchCadence) => {
      if (!detail) return;
      setBusy(true);
      try {
        await merlinWatchesApi.setCadence(detail.watch.id, cadence);
        await refreshAll();
      } catch {
        setNote('Could not change the cadence.');
      } finally {
        setBusy(false);
      }
    },
    [detail, refreshAll],
  );

  const remove = useCallback(async () => {
    if (!detail) return;
    setBusy(true);
    setNote(null);
    try {
      await merlinWatchesApi.remove(detail.watch.id);
      const list = await loadList();
      const next = list && list.length > 0 ? sortWatches(list)[0].id : null;
      setSelected(next ?? (autoSelect === 'newest' ? NEW_WATCH : null));
      setNote('Watch removed. Its readouts are kept.');
    } catch {
      setNote('Could not remove that watch.');
    } finally {
      setBusy(false);
    }
  }, [detail, loadList, autoSelect]);

  return {
    items,
    sorted,
    selected,
    select: setSelected,
    detail,
    html,
    loading,
    error,
    note,
    setNote,
    busy,
    polling,
    refreshAll,
    createWatch,
    refine,
    runNow,
    togglePause,
    setCadence,
    remove,
  };
}
